/**
 * ProfileBoostInlineBanner — Iter-221
 *
 * Compact gradient banner that promotes the $4.99 Profile Boost product
 * inline on talent surfaces (Profile Info, Portfolio, Discoverability).
 * Smaller / less interruptive than the popup modal — designed to be
 * always-visible alongside the user's profile-editing flow so they
 * connect "look at my profile" → "boost my profile".
 *
 * Auto-suppresses when the talent has an active boost so we don't
 * upsell something they already bought.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { monetizationAPI } from '../utils/api';

const ProfileBoostInlineBanner = ({ source = 'profile_info' }) => {
  const navigate = useNavigate();
  const [hasActive, setHasActive] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    monetizationAPI.getHighlightStatus()
      .then(res => {
        if (cancelled) return;
        const status = res?.data;
        if (status?.has_active_boost || status?.active_boost?.is_active) {
          setHasActive(true);
        }
      })
      .catch(() => { /* non-critical */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  if (!loaded || hasActive) return null;

  return (
    <button
      type="button"
      onClick={() => navigate(`/talent/profile-boost?from=${source}`)}
      className="w-full text-left rounded-xl overflow-hidden border-0 shadow-sm hover:shadow-md transition group"
      data-testid="profile-boost-inline-banner"
    >
      <div className="bg-gradient-to-r from-amber-400 via-pink-500 to-purple-600 text-white p-4 flex items-center gap-3">
        <div className="rounded-full bg-white/20 p-2 shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-tight">
            Want more visibility?
          </p>
          <p className="text-xs text-white/90 mt-0.5 leading-tight">
            Boost your profile to more clients for 24 hours — <strong>$4.99</strong>.
          </p>
          {/* Iter-225n — explicit disclaimer per user mandate to remove any
              pay-to-work perception. Applies on every boost surface. */}
          <p
            className="text-[11px] text-white/85 mt-1.5 leading-snug"
            data-testid="boost-free-disclaimer"
          >
            Applying to gigs and getting booked is always free. This optional boost simply increases profile visibility.
          </p>
        </div>
        <ArrowRight className="w-4 h-4 shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </button>
  );
};

export default ProfileBoostInlineBanner;
